import dayjs from "dayjs";
import { Month } from "./Month";

const DateType = Symbol();

export class DateObject {
    [DateType]: any;

    readonly year: number;

    readonly month: number;

    readonly date: number;

    private constructor(year: number, month: number, date: number) {
        this.year = year;
        this.month = month;
        this.date = date;
    }

    equals(other: DateObject): boolean {
        return this.year === other.year && this.month === other.month && this.date === other.date;
    }

    isEqualsOrLessThan(other: DateObject): boolean {
        return (this.equals(other) || this.isLessThan(other));
    }

    isEqualsOrGreaterThan(other: DateObject): boolean {
        return (this.equals(other) || this.isGreaterThan(other));
    }

    isEqualsOrGreaterThanToday(): boolean {
        return this.isEqualsOrGreaterThan(DateObject.today());
    }

    isLessThan(other: DateObject): boolean {
        return dayjs(this.format()).isBefore(dayjs(other.format()));
    }

    isGreaterThan(other: DateObject): boolean {
        return dayjs(this.format()).isAfter(dayjs(other.format()));
    }

    isGreaterThanToday(): boolean {
        return this.isGreaterThan(DateObject.today());
    }

    format(delimiter: string = "-"): string {
        return `${this.year.toString().padStart(4, "0")}${delimiter}${this.month.toString().padStart(2, "0")}${delimiter}${this.date.toString().padStart(2, "0")}`;
    }

    japaneseFormat(zeroPadding: boolean = true): string {
        if (zeroPadding) {
            const year = this.year;
            const month = this.month.toString().padStart(2, "0");
            const date = this.date.toString().padStart(2, "0");
            return `${year}年${month}月${date}日`;
        } else {
            return `${this.year}年${this.month}月${this.date}日`;
        }
    }

    japaneseMonthDateFormat(zeroPadding: boolean = true): string {
        if (zeroPadding) {
            return this.japaneseFormat().substring(5);
        } else {
            return `${this.month}月${this.date}日`;
        }
    }

    japaneseDateFormat(zeroPadding: boolean = true): string {
        if (zeroPadding) {
            return this.japaneseFormat().substring(8);
        } else {
            return `${this.date}日`;
        }
    }

    addDay(day: number): DateObject {
        return DateObject.fromDayJs(dayjs(this.format()).add(day, "day"));
    }

    getMonth(): Month {
        return Month.fromString(`${this.year.toString().padStart(4, "0")}-${this.month.toString().padStart(2, "0")}}`);
    }

    getDayOfWeek(): string {
        return dayjs(this.format()).format("dd");
    }

    getImageColorByDayOfWeek(): string {
        switch (this.getDayOfWeek()) {
            case "日":
                return "#FF0000";
            case "土":
                return "#0000FF";
            default:
                return "#666666";
        }
    }

    private static fromDayJs(dayjs: dayjs.Dayjs): DateObject {
        return DateObject.fromString(dayjs.format("YYYY-MM-DD"));
    }

    static fromString(dateString: string): DateObject {
        assertFormat(dateString);
        return new DateObject(
            Number(dateString.substr(0, 4)),
            Number(dateString.substr(5, 2)),
            Number(dateString.substr(8, 2)),
        );
    }

    static today(): DateObject {
        return DateObject.fromDayJs(dayjs());
    }

    static default(): DateObject {
        return new DateObject(1900, 1, 1);
    }

    static isDateFormat = (str: string): boolean => {
        try {
            const d = new Date(str);
            const year = Number(str.substr(0, 4));
            const month = Number(str.substr(5, 2));
            const date = Number(str.substr(8, 2));

            return (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === date);
        } catch (e) {
            return false;
        }
    };
}

class InvalidDateFormatError extends Error {}

const assertFormat = (str: string) => {
    if (!DateObject.isDateFormat(str)) {
        throw new InvalidDateFormatError("date must be format with 'yyyy-mm-dd'");
    }
};
